import React, { useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import classNames from 'classnames';
import UploadProgressIndicator from './upload-progress-indicator';
import Thumbnail from '../chat/chat-document-thumbnail';
import { MediaDownloadLink, MediaItem } from '../../stores/reducers/media';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { DOWNLOAD_TYPES } from '../../types/constants';
import LoadingSpinner from '../general-components/loading-spinner';
import ISORow from './iso-row';

interface MyProps {
	downloadMedia: (
		media: MediaItem,
		downloadType: string,
		preCallback?: Function,
		postCallback?: Function,
		mediaDownloadLink?: MediaDownloadLink,
	) => Promise<void>;
	ellipses: any;
	isCurrentlyUploading: boolean,
	mediaItem: MediaItem,
	mediaThumbnailURL: string,
	progress: number | undefined,
	thisMediaIsDownloading: boolean,
	isHost: boolean,
	userCanControl: boolean,
}

export default function ExpandedMediaLink(props: MyProps) {
	const {
		downloadMedia,
		ellipses,
		isCurrentlyUploading,
		mediaItem,
		mediaThumbnailURL,
		progress,
		thisMediaIsDownloading,
		isHost,
		userCanControl,
	} = props;

	const [isDownloadingISO, setIsDownloadingISO] = useState<number | undefined>(undefined);

	const isoDownloadReady = useMemo(() => {
		if (!mediaItem.download_links || !mediaItem.iso_url) {
			return false;
		}

		if (
			mediaItem.iso_url?.length === 1
			&& mediaItem.iso_url[0]?.includes('.zip')
		) {
			return false;
		} if (
			mediaItem?.number_of_isos !== undefined && mediaItem?.number_of_isos
			&& mediaItem.number_of_isos + 1 > mediaItem.iso_url?.length
		) {
			return false;
		}
		return true;
	}, [mediaItem?.number_of_isos, mediaItem.iso_url, mediaItem.download_links]);

	const isoDownloads = useMemo(() => (
		mediaItem.download_links
			?.filter((m: MediaDownloadLink) => m.type === 'iso')
			?.sort((a, b) => (a?.label ?? '')?.localeCompare(b?.label ?? ''))
	), [mediaItem.download_links]);

	const handleDownload = async (
		// eslint-disable-next-line @typescript-eslint/no-shadow
		mediaItem: MediaItem,
		index: number,
		mediaDownloadLink?: MediaDownloadLink,
	): Promise<void> => {
		setIsDownloadingISO(index);

		await downloadMedia(
			mediaItem,
			DOWNLOAD_TYPES.SINGLE_ISO,
			undefined,
			setIsDownloadingISO,
			mediaDownloadLink,
		);
	};

	const getSkeletonsForIsosLeft = useMemo(() => {
		let numberOfIsosLeft = !mediaItem?.number_of_isos || Number(mediaItem?.number_of_isos) === 0
			? 3
			: mediaItem.number_of_isos + 1 - (mediaItem.iso_url?.length ?? 0);

		if (numberOfIsosLeft <= 0) {
			numberOfIsosLeft = 3;
		} else if (numberOfIsosLeft >= 5) {
			numberOfIsosLeft = 5;
		}

		const skeletons = [];
		for (let i = 0; i < numberOfIsosLeft; i += 1) {
			skeletons.push((
				<div className="iso-row" key={`iso-skeleton-row-${i}`}>
					<div className="iso-column-1">
						<Skeleton />
					</div>
					<div className="iso-column-2">
						<Skeleton />
					</div>
				</div>
			));
		}
		return skeletons;
	}, [mediaItem.number_of_isos, mediaItem.iso_url]);

	return (
		<div className="expanded-media-item">
			<div className="expanded-media-contents">
				<div className="canvas-recording">
					<div className="thumbnail">
						{(isCurrentlyUploading || !mediaThumbnailURL)
							? (
								<UploadProgressIndicator progress={progress} />
							) : (
								<div
									className={classNames('thumbnail-container', mediaItem?.media_data.fill && 'fill')}
									style={{ background: mediaItem?.media_data.background_color ?? '#151516' }}
								>
									<Thumbnail thumbnail={mediaThumbnailURL} />
								</div>
							)}
					</div>
					<div className="canvas-label">
						Canvas Recording
					</div>
					<div className='canvas-ellipses'>
						{thisMediaIsDownloading && (isHost || userCanControl) && (
							<div className="media-download-spinner">
								<LoadingSpinner height={24} width={24} color={'#878793'} stroke={10} />
							</div>
						)}
						{!thisMediaIsDownloading && ellipses}
					</div>
				</div>

				<div className='expanded-media-header'>
					Feeds
				</div>

				{(isoDownloads?.length ?? 0) > 0 && (
					<div className="iso-downloads">
						{isoDownloads?.map((download: MediaDownloadLink, index: number) => (
							<ISORow
								key={`${index}-${mediaItem?.download_name}`}
								index={index}
								mediaItem={mediaItem}
								canDownload={isHost || userCanControl}
								isDownloading={isDownloadingISO === index}
								download={download}
								downloadCallback={handleDownload}
							/>
						))}
					</div>
				)}

				{!isoDownloadReady && (
					<div className="iso-downloads-skeleton">
						{getSkeletonsForIsosLeft}
					</div>
				)}
			</div>
		</div>
	);
}
